import { type MutableRefObject, useCallback, useRef } from 'react'

import { useLiveEventsProcessor, useNavState } from '@fv/client-components'
import { useQueryClient } from '@fv/client-core'
import type { LiveEvent } from '@fv/models'

import { apiUri } from '@/constants'
import {
  bucketCountKeys,
  messagesQueryKeys,
  opportunityQueryKeys,
  quoteQueryKeys,
  quoteRequestKeys,
} from '@/queryKeys'
import { getStartDateForStatus } from '@/utils/getStartDateForStatus'

import { useAppSettings } from './useAppSettings'

export function useCarrierLiveEvents(carrierId?: string) {
  // Throttle API calls
  const addTimeout = useRef<NodeJS.Timeout>(null)
  const oppUpdateTimeout = useRef<NodeJS.Timeout>(null)
  const quoteUpdateTimeout = useRef<NodeJS.Timeout>(null)
  const queryClient = useQueryClient()
  const settingsQuery = useAppSettings()
  const notifyRefresh = useNavState(s => s.notifyRefresh)

  const debounce = (
    ref: MutableRefObject<NodeJS.Timeout | undefined>,
    cb: () => void,
    ms = 1000,
  ) => {
    if (ref.current) {
      clearTimeout(ref.current)
    }

    ref.current = setTimeout(() => cb(), ms)
  }

  const handleEvent = useCallback(
    (event: LiveEvent) => {
      if (event.eventType === 'message:update') {
        const { loadId, message } = event.data

        queryClient.setQueriesData(
          {
            queryKey: messagesQueryKeys.load(loadId),
          },
          (prev?: Array<{ _id: string }>) => {
            if (!prev) return prev

            return prev
              .filter(m => m._id !== message._id && m._id !== 'newMessage')
              .concat(message)
          },
        )
      }

      if (event.eventType === 'quote:remove') {
        const { loadId, quote } = event.data

        debounce(quoteUpdateTimeout, () => {
          queryClient.invalidateQueries({
            queryKey: quoteRequestKeys.id(quote.quoteRequestId),
          })
          queryClient.invalidateQueries({ queryKey: opportunityQueryKeys.all })
          queryClient.invalidateQueries({ queryKey: bucketCountKeys.all })
        })

        queryClient.setQueriesData(
          { queryKey: quoteQueryKeys.load(loadId) },
          (prev?: Array<{ _id: string }>) =>
            prev?.filter(q => q._id !== quote._id),
        )
      }

      if (event.eventType === 'quote:update') {
        const { loadId, quote } = event.data

        debounce(quoteUpdateTimeout, () => {
          queryClient.invalidateQueries({
            queryKey: quoteRequestKeys.id(quote.quoteRequestId),
          })
          queryClient.invalidateQueries({ queryKey: opportunityQueryKeys.all })
        })

        queryClient.setQueriesData(
          { queryKey: quoteQueryKeys.load(loadId) },
          (prev?: Array<{ _id: string }>) => {
            if (!prev) return prev

            const nextQuotes = prev.filter(q => q._id !== quote._id)

            if (quote.status === 'error') return nextQuotes
            return nextQuotes.concat(quote)
          },
        )
      }

      if (event.eventType === 'opportunity:add') {
        const status = 'open'

        debounce(addTimeout, () => {
          queryClient.invalidateQueries({
            queryKey: opportunityQueryKeys.byStatus(status),
          })
          queryClient.invalidateQueries({
            queryKey: opportunityQueryKeys.search(),
          })

          queryClient.invalidateQueries({
            queryKey: bucketCountKeys.byStartDate(
              getStartDateForStatus(status),
            ),
          })
        })
      }

      if (event.eventType === 'opportunity:update') {
        const { quoteRequestId, countsChanged } = event.data

        debounce(oppUpdateTimeout, () => {
          queryClient.invalidateQueries({
            queryKey: quoteRequestKeys.id(quoteRequestId),
          })
          queryClient.invalidateQueries({ queryKey: opportunityQueryKeys.all })
          countsChanged &&
            queryClient.invalidateQueries({ queryKey: bucketCountKeys.all })
        })
      }

      if (event.eventType === 'system:reset') {
        notifyRefresh()
      }
    },
    [queryClient, notifyRefresh],
  )

  useLiveEventsProcessor({
    authenticateUri:
      carrierId && `${apiUri}/live-events?carrierId=${carrierId}`,
    handleEvent,
    liveEventsUri: settingsQuery.data?.liveEventsUri,
  })
}
